import { useState, useEffect, useCallback } from "react";
import { useBackButton } from "@/hooks/useBackButton";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, LogOut, Package, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Session } from "@supabase/supabase-js";

interface Order {
  id: string;
  created_at: string;
  items: any;
  total: number;
  status: string;
  payment_method: string;
}

const statusLabels: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  delivered: "Entregue",
  cancelled: "Cancelado",
};

const statusColors: Record<string, string> = {
  pending: "bg-yellow-500/20 text-yellow-400",
  confirmed: "bg-blue-500/20 text-blue-400",
  delivered: "bg-green-500/20 text-green-400",
  cancelled: "bg-red-500/20 text-red-400",
};

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const UserDropdown = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const closeDropdown = useCallback(() => setOpen(false), []);
  useBackButton(open, closeDropdown, "user-dropdown");
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (open && session?.user?.email) {
      setLoading(true);
      supabase
        .from("orders")
        .select("id, created_at, items, total, status, payment_method")
        .eq("customer_email", session.user.email)
        .order("created_at", { ascending: false })
        .limit(20)
        .then(({ data }) => {
          setOrders((data as Order[]) || []);
          setLoading(false);
        });
    }
  }, [open, session]);

  if (!session) {
    return (
      <a href="/auth" className="p-2 text-foreground hover:text-primary transition-colors" aria-label="Entrar na conta">
        <User className="w-5 h-5" />
      </a>
    );
  }

  const user = session.user;
  const displayName = user.user_metadata?.full_name || user.email?.split("@")[0] || "Usuário";
  const avatarUrl = user.user_metadata?.avatar_url;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setOpen(false);
    navigate("/");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="p-2 text-foreground hover:text-primary transition-colors">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
          ) : (
            <User className="w-5 h-5" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-card border-border" align="end" sideOffset={8}>
        <Tabs defaultValue="perfil" className="w-full">
          <TabsList className="w-full rounded-none border-b border-border bg-secondary/50">
            <TabsTrigger value="perfil" className="flex-1 text-xs">Meu Perfil</TabsTrigger>
            <TabsTrigger value="pedidos" className="flex-1 text-xs">Meus Pedidos</TabsTrigger>
          </TabsList>

          <TabsContent value="perfil" className="p-4 space-y-4 mt-0">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-12 h-12 rounded-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-semibold text-sm text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>

            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Provedor</span>
                <span className="text-foreground capitalize">{user.app_metadata?.provider || "email"}</span>
              </div>
              <div className="flex justify-between">
                <span>Membro desde</span>
                <span className="text-foreground">
                  {new Date(user.created_at).toLocaleDateString("pt-BR")}
                </span>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-destructive/10 text-destructive text-sm font-medium hover:bg-destructive/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair da conta
            </button>
          </TabsContent>

          <TabsContent value="pedidos" className="mt-0">
            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Carregando...</div>
              ) : orders.length === 0 ? (
                <div className="p-6 text-center space-y-2">
                  <Package className="w-8 h-8 mx-auto text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">Nenhum pedido encontrado</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {orders.map((order) => {
                    const items = Array.isArray(order.items) ? order.items : [];
                    const itemNames = items.map((i: any) => i.name || i.phone_name || "Item").join(", ");
                    return (
                      <div key={order.id} className="p-3 hover:bg-secondary/30 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-foreground truncate">{itemNames}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {new Date(order.created_at).toLocaleDateString("pt-BR")} · {order.payment_method}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-xs font-semibold text-foreground">{formatCurrency(order.total)}</p>
                            <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${statusColors[order.status] || "bg-muted text-muted-foreground"}`}>
                              {statusLabels[order.status] || order.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};

export default UserDropdown;
